import { useContext } from "react";
import { AuthContext, type AuthState } from "./auth-context";
import type { Role } from "./types";

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** Convenience: true when current user has any of the allowed roles. */
export function useHasRole(...allowed: Role[]): boolean {
  const { user } = useAuth();
  if (!user) return false;
  return allowed.includes(user.role);
}

/**
 * Per-app write gate (§51). True when the user can edit the given app:
 * Account Admins (account role 'admin') write every app; otherwise the
 * per-app level must be 'ads_manager' or 'admin'. Pass `null`/`undefined`
 * (e.g. the "All apps" view, or before an entity loads) → falls back to the
 * coarse "can write somewhere" flag so writer affordances aren't hidden for a
 * per-app writer; the backend still enforces the specific target app.
 */
export function useCanWriteApp(appId: number | null | undefined): boolean {
  const { user } = useAuth();
  if (!user) return false;
  if (user.role === "admin") return true;
  if (appId == null) return !!user.has_app_write;
  const lvl = user.app_access?.find((a) => a.app_id === appId)?.level;
  return lvl === "ads_manager" || lvl === "admin";
}
